
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Message {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: 'pending' | 'replied';
  created_at: string;
  replied_at?: string;
  type: 'contact' | 'demo';
}

const AdminMessages = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [filteredMessages, setFilteredMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    search: ''
  });
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    loadMessages();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [messages, filters]);

  const loadMessages = async () => {
    try {
      setIsLoading(true);
      
      // Contact ve Demo tablolarından mesajları çek
      const [contactRes, demoRes] = await Promise.all([
        supabase.from('contact_submissions').select('*'),
        supabase.from('demo_requests').select('*')
      ]);

      const allMessages: Message[] = [
        ...(contactRes.data || []).map(item => ({
          id: item.id,
          name: item.name || 'İsimsiz',
          email: item.email,
          subject: item.subject || 'İletişim Formu',
          message: item.message || '',
          status: 'pending' as const,
          type: 'contact' as const,
          created_at: item.created_at
        })),
        ...(demoRes.data || []).map(item => ({
          id: item.id,
          name: item.name || 'İsimsiz',
          email: item.email,
          subject: 'Demo Talebi',
          message: item.message || 'Demo talep edildi',
          status: 'pending' as const,
          type: 'demo' as const,
          created_at: item.created_at
        }))
      ];

      // Tarihe göre sırala (en yeni önce)
      allMessages.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      
      setMessages(allMessages);
    } catch (error) {
      console.error('Mesajlar yüklenirken hata:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...messages];

    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(msg => msg.status === filters.status);
    }

    // Type filter
    if (filters.type !== 'all') {
      filtered = filtered.filter(msg => msg.type === filters.type);
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(msg =>
        msg.name.toLowerCase().includes(searchLower) ||
        msg.email.toLowerCase().includes(searchLower) ||
        msg.subject.toLowerCase().includes(searchLower) ||
        msg.message.toLowerCase().includes(searchLower)
      );
    }

    setFilteredMessages(filtered);
  };

  const markAsReplied = (messageId: string) => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId 
        ? { ...msg, status: 'replied' as const, replied_at: new Date().toISOString() }
        : msg
    ));
  };

  const markAllAsRead = () => {
    setMessages(prev => prev.map(msg => ({ ...msg, status: 'replied' as const })));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'replied': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Bekliyor';
      case 'replied': return 'Yanıtlandı';
      default: return 'Bilinmiyor';
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case 'contact': return 'İletişim';
      case 'demo': return 'Demo Talebi';
      default: return 'Bilinmiyor';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'contact': return 'ri-mail-line';
      case 'demo': return 'ri-presentation-line';
      default: return 'ri-message-line';
    }
  };

  const openDetailModal = (message: Message) => {
    setSelectedMessage(message);
    setShowDetailModal(true);
  };

  const pendingCount = messages.filter(msg => msg.status === 'pending').length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Mesajlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gelen Mesajlar</h1>
            <p className="text-gray-600 mt-1">Müşterilerden gelen tüm mesajları buradan yönetin</p>
          </div>
          <div className="flex items-center space-x-3">
            {pendingCount > 0 && (
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                {pendingCount} yanıt bekliyor
              </span>
            )}
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              {filteredMessages.length} mesaj
            </span>
            {pendingCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-check-double-line mr-2"></i>
                Tümünü Okundu İşaretle
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Arama</label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Ad, e-posta, konu ara..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Durum</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Tüm Durumlar</option>
              <option value="pending">Yanıt Bekliyor</option>
              <option value="replied">Yanıtlandı</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tür</label>
            <select
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Tüm Türler</option>
              <option value="contact">İletişim</option>
              <option value="demo">Demo Talebi</option>
            </select>
          </div>
        </div>
      </div>

      {/* Messages List */}
      <div className="space-y-4">
        {filteredMessages.map((message) => (
          <div
            key={message.id}
            className={`bg-white rounded-2xl p-6 shadow-lg border transition-all duration-300 hover:shadow-xl cursor-pointer ${
              message.status === 'pending' ? 'border-yellow-200 bg-yellow-50/30' : 'border-gray-100'
            }`}
            onClick={() => openDetailModal(message)}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-1">
                {/* Message Icon */}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  message.type === 'contact' ? 'bg-blue-100' : 'bg-purple-100'
                }`}>
                  <i className={`${getTypeIcon(message.type)} text-xl ${
                    message.type === 'contact' ? 'text-blue-600' : 'text-purple-600'
                  }`}></i>
                </div>

                {/* Message Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="font-semibold text-gray-900">{message.name}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      message.type === 'contact' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                    }`}>
                      {getTypeText(message.type)}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(message.status)}`}>
                      {getStatusText(message.status)}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-2">{message.email}</p>
                  
                  <h4 className="font-medium text-gray-900 mb-2">{message.subject}</h4>
                  
                  <p className="text-gray-700 line-clamp-2">{message.message}</p>
                  
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-gray-500">
                      {new Date(message.created_at).toLocaleDateString('tr-TR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                    
                    {message.status === 'replied' && message.replied_at && (
                      <span className="text-sm text-green-600 flex items-center">
                        <i className="ri-check-double-line mr-1"></i>
                        Yanıtlandı
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-2 ml-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openDetailModal(message);
                  }}
                  className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                  title="Detayları Görüntüle"
                >
                  <i className="ri-eye-line"></i>
                </button>
                
                <a
                  href={`mailto:${message.email}?subject=Re: ${message.subject}`}
                  onClick={(e) => e.stopPropagation()}
                  className="w-8 h-8 flex items-center justify-center text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                  title="E-posta Gönder"
                >
                  <i className="ri-reply-line"></i>
                </a>
                
                {message.status === 'pending' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      markAsReplied(message.id);
                    }}
                    className="w-8 h-8 flex items-center justify-center text-yellow-600 hover:bg-yellow-100 rounded-lg transition-colors"
                    title="Yanıtlandı Olarak İşaretle"
                  >
                    <i className="ri-check-line"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredMessages.length === 0 && (
        <div className="bg-white rounded-2xl p-12 shadow-lg border border-gray-100 text-center">
          <i className="ri-inbox-line text-4xl text-gray-400 mb-4"></i>
          <p className="text-gray-600">Henüz mesaj bulunmuyor</p>
        </div>
      )}

      {/* Message Detail Modal */}
      {showDetailModal && selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Mesaj Detayları</h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              <div className="space-y-6">
                {/* Gönderen Bilgileri */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Gönderen Bilgileri</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ad Soyad</label>
                      <p className="text-gray-900">{selectedMessage.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">E-posta</label>
                      <p className="text-gray-900">{selectedMessage.email}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Mesaj Türü</label>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        selectedMessage.type === 'contact' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                      }`}>
                        {getTypeText(selectedMessage.type)}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tarih</label>
                      <p className="text-gray-900">
                        {new Date(selectedMessage.created_at).toLocaleDateString('tr-TR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Konu */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Konu</h3>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-gray-900 font-medium">{selectedMessage.subject}</p>
                  </div>
                </div>

                {/* Mesaj İçeriği */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Mesaj İçeriği</h3>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.message}</p>
                  </div>
                </div>

                {/* Durum Yönetimi */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Durum</h3>
                  <div className="flex items-center space-x-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedMessage.status)}`}>
                      {getStatusText(selectedMessage.status)}
                    </span>
                    {selectedMessage.status === 'pending' && (
                      <button
                        onClick={() => {
                          markAsReplied(selectedMessage.id);
                          setShowDetailModal(false);
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-check-line mr-2"></i>
                        Yanıtlandı Olarak İşaretle
                      </button>
                    )}
                  </div>
                </div>

                {/* İletişim Butonları */}
                <div className="flex space-x-3 pt-4 border-t border-gray-200">
                  <a
                    href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center whitespace-nowrap"
                  >
                    <i className="ri-reply-line mr-2"></i>
                    Yanıtla
                  </a>
                  <a
                    href={`mailto:${selectedMessage.email}`}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-center whitespace-nowrap"
                  >
                    <i className="ri-mail-line mr-2"></i>
                    Yeni E-posta
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
